import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AppBreadcrumbs from 'components/AppBreadcrumbs';
import PageLayout from 'layouts/PageLayout';
import NotFound from 'pages/NotFound';
import LanguageStatus from 'pages/Templates/Edit/LanguageStatus';
import { makeStyles } from '@material-ui/core/styles';
import { useAsyncFn } from 'react-use';
import { useDispatch } from 'react-redux';
import { getTrainingPartImpactedSessions } from 'http/trainingParts';
import {
  getMoodleTrainingPartLanguageLinks,
  replaceMoodleTrainingPartLanguageLinks,
} from 'http/moodle';
import { useHistory, useRouteMatch, useLocation } from 'react-router-dom';
import { routesAliases, buildProvisionedModuleEditPath } from 'router/routesAliases';
import { useTranslation } from 'react-i18next';
import { actions as modalActions } from 'redux/modal';
import { getConfirmationModalConfig, openEntityLifecycleConfirm, resolveApiErrorMessage } from 'utils';
import TrainingPartForm from 'pages/TrainingModules/Edit/TrainingPartForm';
import TrainingPartFilesTable from 'pages/TrainingModules/Edit/TrainingPartFilesTable';
import { mapMoodleLinksToFormSelection } from 'pages/TrainingModules/Edit/helpers/buildMoodleLanguageLinksPayload';
import { hasTrainingPartScheduleImpactFieldsChanged } from 'pages/TrainingModules/Edit/helpers/hasTrainingPartScheduleImpactFieldsChanged';
import { useSessionsImpactConfirm } from 'pages/TrainingModules/Edit/helpers/sessionsImpactConfirm';
import {
  cancelProvisionedTrainingPart,
  getProvisionedTrainingModule,
  getProvisionedTrainingPart,
  getProvisionedTrainingPartLmsLanguageStatus,
  launchProvisionedTrainingPart,
  syncProvisionedTrainingPartLmsLanguages,
  updateProvisionedTrainingPart,
} from 'http/hyperAdminProvisioned';

const TRAINING_METHODS = ['theory', 'ojt', 'practical'];

const METHOD_TAB_INDEX = {
  theory: 1,
  practical: 2,
  ojt: 3,
};

const useStyles = makeStyles(theme => ({
  pageContent: {
    padding: theme.spacing(5, 5),
  },
}));

const EditPart = ({ routeProps }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const routerMatch = useRouteMatch();
  const { t } = useTranslation('common');
  const { companyId, moduleId, trainingPartId, trainingMethod } = routerMatch.params;
  const numericCompanyId = Number(companyId);
  const [notFoundError, setNotFoundError] = useState(false);
  const [moduleData, setModuleData] = useState(null);
  const [partData, setPartData] = useState(null);
  const [languageStatus, setLanguageStatus] = useState(null);
  const openSessionsImpactConfirm = useSessionsImpactConfirm();

  const backUrl = buildProvisionedModuleEditPath(companyId, moduleId);
  const backState = {
    activeTab: METHOD_TAB_INDEX[trainingMethod] ?? 0,
    returnTab: location.state?.returnTab,
  };

  const isValidMethod = TRAINING_METHODS.includes(trainingMethod);
  const isSystemModule = Boolean(moduleData?.isFromSystem);
  const showLanguageSyncPanel =
    isSystemModule &&
    trainingMethod === 'theory' &&
    Boolean(partData?.isFromSystem) &&
    Boolean(partData?.hasMoodleLink);

  const [fetchState, fetchData] = useAsyncFn(async () => {
    const [moduleResult, partResult] = await Promise.all([
      getProvisionedTrainingModule(numericCompanyId, moduleId),
      getProvisionedTrainingPart(numericCompanyId, trainingPartId),
    ]);

    if (moduleResult?.error) {
      if (moduleResult.statusCode === 404) {
        setNotFoundError(true);
      }
      return { moduleResult, partResult };
    }

    if (partResult?.error) {
      if (partResult.statusCode === 404) {
        setNotFoundError(true);
      }
      return { moduleResult, partResult };
    }

    const moduleMatches = Number(partResult.trainingModuleId) === Number(moduleId);
    const methodMatches = partResult.trainingMethod === trainingMethod;

    if (!moduleMatches || !methodMatches) {
      setNotFoundError(true);
      return { moduleResult, partResult };
    }

    let enrichedPart = partResult;
    if (trainingMethod === 'theory') {
      const moodleLinks = await getMoodleTrainingPartLanguageLinks(trainingPartId);
      if (!moodleLinks?.error && !moodleLinks?.statusCode) {
        enrichedPart = {
          ...partResult,
          moodleLanguageLinks: mapMoodleLinksToFormSelection(
            moodleLinks,
            partResult.code,
            partResult.version,
            partResult.revision,
          ),
        };
      }
      if (moduleResult.isFromSystem && partResult.isFromSystem) {
        const statusResult = await getProvisionedTrainingPartLmsLanguageStatus(numericCompanyId, trainingPartId);
        if (!statusResult?.error && !statusResult?.statusCode) {
          setLanguageStatus(statusResult);
        }
      }
    }

    setModuleData(moduleResult);
    setPartData(enrichedPart);
    setNotFoundError(false);
    return { moduleResult, partResult: enrichedPart };
  }, [moduleId, numericCompanyId, trainingMethod, trainingPartId]);

  useEffect(() => {
    if (isValidMethod) {
      setNotFoundError(false);
      setModuleData(null);
      setPartData(null);
      fetchData();
    }
  }, [fetchData, isValidMethod, moduleId, trainingMethod, trainingPartId]);

  const breadCrumbsItems = useMemo(
    () => [
      {
        title: t(routeProps.parentTitle),
        link: routeProps.parentPath,
      },
      {
        title: t('routes.editCompany'),
        link: `${routesAliases.editCompany}/${companyId}`,
      },
      {
        title: moduleData?.name || t('routes.editTrainingModule'),
        link: { pathname: backUrl, state: backState },
      },
      {
        title: t('trainingParts.editPageTitle'),
        isActive: true,
      },
    ],
    [backState, backUrl, companyId, moduleData?.name, routeProps.parentPath, routeProps.parentTitle, t],
  );

  const [submittingState, savePart] = useAsyncFn(
    async payload => {
      const { moodleLanguageLinks, ...partPayload } = payload;

      const persistPart = async () => {
        const result = await updateProvisionedTrainingPart(numericCompanyId, trainingPartId, partPayload);
        if (result?.error || result?.statusCode) {
          return result;
        }

        if (trainingMethod === 'theory' && moodleLanguageLinks) {
          const linkResult = await replaceMoodleTrainingPartLanguageLinks(trainingPartId, moodleLanguageLinks);
          if (linkResult?.error || linkResult?.statusCode) {
            return linkResult;
          }
        }

        history.push(backUrl, backState);
        return result;
      };

      const isDraftPart = partData?.status === 'draft';
      const shouldPreviewImpact = !isDraftPart && hasTrainingPartScheduleImpactFieldsChanged(partData, partPayload);

      if (!shouldPreviewImpact) {
        return persistPart();
      }

      const impacted = await getTrainingPartImpactedSessions(trainingPartId);
      if (impacted?.error || impacted?.statusCode) {
        return impacted;
      }

      if (!impacted?.items?.length) {
        return persistPart();
      }

      return new Promise(resolve => {
        openSessionsImpactConfirm({
          sessions: impacted.items,
          onConfirm: async () => {
            const result = await persistPart();
            resolve(result);
          },
          onCancel: () => resolve(undefined),
        });
      });
    },
    [backState, backUrl, history, numericCompanyId, openSessionsImpactConfirm, partData, trainingMethod, trainingPartId],
  );

  const closeModal = useCallback(() => {
    dispatch(modalActions.closeModal());
  }, [dispatch]);

  const showErrorModal = useCallback(
    message => {
      const [config, element] = getConfirmationModalConfig({
        confirmTitle: t('buttons.ok'),
        confirmAction: closeModal,
        rejectAction: closeModal,
        message: resolveApiErrorMessage(message, {
          t,
          fallbackMessage: t('errors.serverError'),
        }),
      });
      dispatch(modalActions.openModal(config, element));
    },
    [closeModal, dispatch, t],
  );

  const [languageSyncState, syncLanguages] = useAsyncFn(
    async id => {
      const result = await syncProvisionedTrainingPartLmsLanguages(numericCompanyId, id);
      if (!result?.error && !(result?.statusCode && result.statusCode >= 400)) {
        await fetchData();
        return result;
      }
      showErrorModal(result?.message);
      return result;
    },
    [fetchData, numericCompanyId, showErrorModal],
  );

  const [launchState, launchPart] = useAsyncFn(async () => {
    const result = await launchProvisionedTrainingPart(numericCompanyId, trainingPartId);
    if (!result?.error && !result?.statusCode) {
      await fetchData();
      return result;
    }
    showErrorModal(result.message);
    return result;
  }, [fetchData, numericCompanyId, showErrorModal, trainingPartId]);

  const [cancelState, cancelPart] = useAsyncFn(async () => {
    const result = await cancelProvisionedTrainingPart(numericCompanyId, trainingPartId);
    if (!result?.error && !result?.statusCode) {
      await fetchData();
      return result;
    }
    showErrorModal(result.message);
    return result;
  }, [fetchData, numericCompanyId, showErrorModal, trainingPartId]);

  const confirmLaunchPart = useCallback(() => {
    openEntityLifecycleConfirm({
      dispatch,
      closeModal,
      t,
      i18nPrefix: 'trainingParts',
      action: 'launch',
      onConfirm: launchPart,
    });
  }, [closeModal, dispatch, launchPart, t]);

  const confirmCancelPart = useCallback(() => {
    openEntityLifecycleConfirm({
      dispatch,
      closeModal,
      t,
      i18nPrefix: 'trainingParts',
      action: 'cancel',
      onConfirm: cancelPart,
    });
  }, [cancelPart, closeModal, dispatch, t]);

  if (!isValidMethod || notFoundError) {
    return <NotFound />;
  }

  const additionSections = [];

  if (showLanguageSyncPanel) {
    additionSections.push({
      contentHeader: t('trainingParts.lmsLanguages.contentLanguages', 'Content languages'),
      component: <LanguageStatus status={languageStatus} />,
      actions: [
        {
          type: 'button',
          icon: 'sync',
          title: t('trainingParts.lmsLanguages.syncLmsLanguages', 'Sync LMS languages'),
          onClick: () => syncLanguages(trainingPartId),
          disabled: languageSyncState.loading,
        },
      ],
    });
  }

  if (partData?.id) {
    additionSections.push({
      component: (
        <TrainingPartFilesTable
          trainingPartId={partData.id}
          canAddFiles={!isSystemModule}
          canDeleteFiles={!isSystemModule}
          canManageVisibility={!isSystemModule}
          isSystemModule={isSystemModule}
        />
      ),
    });
  }

  return (
    <PageLayout
      pageTitle={<AppBreadcrumbs items={breadCrumbsItems} />}
      classes={{ content: classes.pageContent }}
      showLoader={fetchState.loading || languageSyncState.loading || !moduleData || !partData}
      additionSections={additionSections}
    >
      {moduleData && partData ? (
        <TrainingPartForm
          mode="edit"
          layout="page"
          trainingMethod={trainingMethod}
          trainingModuleCode={moduleData.code}
          initialValues={partData}
          backUrl={backUrl}
          backState={backState}
          submitting={submittingState.loading || launchState.loading || cancelState.loading}
          onSubmitForm={savePart}
          onLaunch={confirmLaunchPart}
          onCancelEntity={confirmCancelPart}
          isReadOnly={false}
        />
      ) : (
        <span />
      )}
    </PageLayout>
  );
};

EditPart.propTypes = {
  routeProps: PropTypes.shape({
    parentPath: PropTypes.string,
    parentTitle: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
};

export default EditPart;
